import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Building2, Shield, FileCheck, Check, X, AlertTriangle, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * BUSINESS LANDING PAGE — LOCKED DIRECTIVE
 * 
 * Updated to align with Sponsorship System.
 * NO advertising language, NO tracking, NO targeting.
 * 
 * This page explains:
 * - What Business membership is
 * - The difference between membership and sponsorship
 * - How to apply for sponsorship
 */

export default function BusinessLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-40"
        style={{ 
          backgroundColor: 'var(--fy-surface-card)', 
          borderBottom: '1px solid var(--fy-border-subtle)' 
        }}
      >
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden" 
                style={{ backgroundColor: 'var(--fy-background-secondary)' }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" 
                  alt="LikeKind" 
                  className="w-8 h-8 object-cover" 
                />
              </div>
              <span className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>LikeKind</span>
            </Link>
            <Button
              onClick={() => navigate('/membership')}
              size="sm"
              className="rounded-full"
              style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)' }}
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: '#6B728015' }}
          >
            <Building2 className="w-8 h-8" style={{ color: '#6B7280' }} />
          </div>
          
          <h1 
            className="text-3xl sm:text-4xl font-bold mb-4"
            style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}
          >
            Business Presence on LikeKind
          </h1>
          
          <p 
            className="text-lg mb-8"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            Represent your business with integrity. No ads. No tracking. No engagement games.
            Just a calm, honest way to be present where real people gather.
          </p>

          <Button
            onClick={() => navigate('/membership/business')}
            className="rounded-full px-8 h-12 font-bold"
            style={{ backgroundColor: '#6B7280', color: '#FFFFFF' }}
          >
            Learn More
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* What Business Membership Is */}
      <section 
        className="py-12 px-4"
        style={{ backgroundColor: 'var(--fy-surface-muted)' }}
      >
        <div className="max-w-2xl mx-auto">
          <h2 
            className="text-xl font-bold text-center mb-8"
            style={{ color: 'var(--fy-text-primary)' }}
          >
            What Business Membership Provides
          </h2>
          
          <div className="grid gap-4">
            {[
              {
                title: 'Eligibility to Apply for Sponsorship',
                description: 'Business membership makes you eligible to apply for vetted sponsorship placements. Approval is manual and not guaranteed.',
              },
              {
                title: 'Real Business Identity',
                description: 'Represent your business authentically. No anonymous accounts. No bots. Just real businesses serving real communities.',
              },
              {
                title: 'Commons Stewardship',
                description: 'Where applicable, businesses can participate in Commons stewardship — helping to maintain community spaces.',
              },
              {
                title: 'Long-term Group Hosting',
                description: 'Host ongoing groups for your community. Non-social, non-algorithmic — just practical coordination.',
              },
            ].map((item, i) => (
              <div 
                key={i}
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'var(--fy-surface-card)' }}
              >
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#7A8F5B' }} />
                  <div>
                    <h3 className="font-bold text-sm mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                      {item.title}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Business Membership Is NOT */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h2 
            className="text-xl font-bold text-center mb-8"
            style={{ color: 'var(--fy-text-primary)' }}
          >
            What Business Membership Is NOT
          </h2>
          
          <div 
            className="p-6 rounded-2xl"
            style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
          >
            <p 
              className="text-sm mb-4 text-center"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              LikeKind is not an advertising platform. Business membership does not include:
            </p>
            
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Advertising tools',
                'Analytics or tracking',
                'Behavioral targeting',
                'Engagement metrics',
                'Impression counts',
                'Guaranteed visibility',
                'Promotion boosting',
                'Audience insights',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <X className="w-4 h-4 flex-shrink-0" style={{ color: '#dc2626' }} />
                  <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sponsorship Section */}
      <section 
        className="py-12 px-4"
        style={{ backgroundColor: 'var(--fy-surface-muted)' }}
      >
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Shield className="w-6 h-6" style={{ color: '#7A8F5B' }} />
            <h2 
              className="text-xl font-bold"
              style={{ color: 'var(--fy-text-primary)' }}
            >
              About Sponsorships
            </h2>
          </div>
          
          <p 
            className="text-center text-sm mb-8"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            Sponsorships are separate from membership. They are manual, vetted, and governed by strict rules.
          </p>

          <div 
            className="p-6 rounded-2xl mb-6"
            style={{ backgroundColor: 'var(--fy-surface-card)' }}
          >
            <h3 
              className="font-bold text-sm mb-4"
              style={{ color: 'var(--fy-text-primary)' }}
            >
              All sponsorship placements are:
            </h3>
            
            <div className="space-y-2">
              {[
                'Static — no dynamic content, no autoplay',
                'Pre-paid — payment before placement',
                'Reviewed by humans — no auto-approval',
                'Time-limited — 30-day cycles, re-review required',
                'Immutable — any change requires re-approval',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <FileCheck className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A8F5B' }} />
                  <span className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div 
            className="p-4 rounded-xl text-center"
            style={{ backgroundColor: 'var(--fy-background-primary)' }}
          >
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
              Key distinction:
            </p>
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              <strong>Sponsorship ≠ advertising.</strong> Placement ≠ promotion. Visibility ≠ influence.
            </p>
          </div>
        </div>
      </section>

      {/* Child Safety Section */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div 
            className="p-6 rounded-2xl"
            style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: '#dc2626' }} />
              <div>
                <h3 
                  className="font-bold mb-2"
                  style={{ color: '#991b1b' }}
                >
                  Child & Family Safety
                </h3>
                <p 
                  className="text-sm mb-3"
                  style={{ color: '#991b1b' }}
                >
                  LikeKind serves families and schools. Safety is non-negotiable.
                </p>
                <ul className="text-sm space-y-1" style={{ color: '#991b1b' }}>
                  <li>• No child-directed sponsorship content permitted</li>
                  <li>• No tracking or data collection from minors</li>
                  <li>• Violations result in immediate permanent ban</li>
                  <li>• No exceptions. No appeals for child-safety issues.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Policy Links */}
      <section 
        className="py-12 px-4"
        style={{ backgroundColor: 'var(--fy-surface-muted)' }}
      >
        <div className="max-w-2xl mx-auto">
          <h2 
            className="text-lg font-bold text-center mb-6"
            style={{ color: 'var(--fy-text-primary)' }}
          >
            Important Policies
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <a
              href="/sponsor-policy"
              className="flex items-center justify-between p-4 rounded-xl transition-colors hover:opacity-80"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
            >
              <div>
                <p className="font-bold text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                  Sponsor Policy
                </p>
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                  Rules governing sponsorships
                </p>
              </div>
              <ExternalLink className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </a>
            
            <a
              href="/about-terms"
              className="flex items-center justify-between p-4 rounded-xl transition-colors hover:opacity-80"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
            >
              <div>
                <p className="font-bold text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                  Trust & Safety
                </p>
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                  How we protect users
                </p>
              </div>
              <ExternalLink className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </a>
            
            <a
              href="/faq/business"
              className="flex items-center justify-between p-4 rounded-xl transition-colors hover:opacity-80"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
            >
              <div>
                <p className="font-bold text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                  Business FAQ
                </p>
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                  Common questions answered
                </p>
              </div>
              <ExternalLink className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </a>
            
            <a
              href="/privacy"
              className="flex items-center justify-between p-4 rounded-xl transition-colors hover:opacity-80"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
            >
              <div>
                <p className="font-bold text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                  Privacy Policy
                </p>
                <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                  How we handle data
                </p>
              </div>
              <ExternalLink className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-xl mx-auto text-center">
          <h2 
            className="text-2xl font-bold mb-4"
            style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}
          >
            Ready to get started?
          </h2>
          <p 
            className="text-sm mb-8"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            Business membership gives you a foundation. Sponsorship eligibility. Real presence. No gimmicks.
          </p>
          
          <Button
            onClick={() => navigate('/membership/business')}
            className="rounded-full px-8 h-12 font-bold"
            style={{ backgroundColor: '#6B7280', color: '#FFFFFF' }}
          >
            View Business Membership
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer 
        className="py-8 px-4 text-center"
        style={{ borderTop: '1px solid var(--fy-border-subtle)' }}
      >
        <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
          © 2026 LikeKind. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
